import { useDateNow, useDispatch, useReduxState } from 'hooks';
import { getFormattedDate } from 'lib/common';
import {
  ApiDownloadButton,
  ApiPostButton,
  Button,
  ConfirmButton,
  ProgressBar,
  SendToEmailButton
} from 'components/common';
import { CourseCompletionCustomContent } from 'components/courses/common';
import { Dropdown } from 'components/common/dropdown';
import { FollowUsersDownloadAnswers } from 'components/courses/follow';
import { ReaderCompletionFormBadge, ReaderCompletionFormRequirement } from '.';

import type { CourseCompletion } from 'shared-types';
import type { ButtonSize } from 'components/common/Button';

const ReaderCompletionFormResult = ({
  completion,
  fontSize,
  onRetry
}: {
  completion: CourseCompletion;
  fontSize: string;
  onRetry: () => Promise<boolean>;
}) => {
  const { dateNow } = useDateNow();

  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: {
      chapterData,
      readOnly,
      readerCourseData: { course, courseRequirements }
    },
    serverConfig: {
      courseCompletionCustomContent,
      courseCompletionHideRequirements,
      courseCompletionHideResults,
      videoRequirementsType
    },
    theme,
    user
  } = useReduxState();

  // extract videosWatched and videosWatchedRequirement numbers from
  // the string that is stored in the database
  let videosWatched;
  let videosWatchedRequirement;
  if (videoRequirementsType === 'individual' && completion.videosWatched) {
    // extract numbers from completion.videosWatched string
    const [watchedString, requirementString] =
      completion.videosWatched.split('/');
    videosWatched = parseInt(watchedString, 10);
    videosWatchedRequirement = parseInt(requirementString, 10);
  }

  return (
    <div className="result">
      <div
        className="content"
        style={{
          fontSize: {
            small: '1.4rem',
            medium: '1.5rem',
            large: '1.6rem',
            huge: '2rem'
          }[fontSize]
        }}
      >
        {completion && completion.status === 'failed' ? (
          <div className="info">
            <div className="badge">
              <ReaderCompletionFormBadge
                category="error"
                icon="exclamation"
                size="small"
              />
            </div>

            {!completion.inactive && course.completionAllowRetries ? (
              // completion failed and user can retry
              <>
                <div className="title">{messages.readerCompletionFailed}</div>
                <div className="description">
                  {messages.readerCompletionFailedDescription}
                </div>
              </>
            ) : !completion.inactive && !course.completionAllowRetries ? (
              // completion failed and user can not retry
              <>
                <div className="title">{messages.readerCompletionFailed}</div>
                <div className="description">
                  {`${messages.readerCompletionFailedDescription} 
                      ${messages.readerCompletionFailedDescriptionContactAdmin}`}
                </div>
              </>
            ) : (
              // completion just failed and user can retry
              <>
                <div className="title">{messages.readerCompletionFailed}</div>
                <div className="description">
                  {messages.readerCompletionFailedDescription}
                  {` ${messages.readerCompletionFailedTryAgain}`}
                </div>
              </>
            )}
          </div>
        ) : completion && completion.status === 'waiting-for-reviews' ? (
          <div className="info">
            <div className="badge">
              <ReaderCompletionFormBadge
                category="light"
                icon="hourglass-start"
                size="small"
              />
            </div>
            <div className="title">{messages.completionsWaitingForReviews}</div>
            <div className="description">
              {messages.readerCompletionWaitingDescription}
            </div>
          </div>
        ) : completion && completion.status === 'success' ? (
          <div className="info">
            {completion.expirationDate &&
            Date.parse(completion.expirationDate as string) < dateNow ? (
              <>
                <div className="badge">
                  <ReaderCompletionFormBadge category="warning" icon="award" />
                </div>
                <div className="title">{messages.completionsExpired}</div>
              </>
            ) : (
              <>
                <div className="badge">
                  <ReaderCompletionFormBadge
                    animateSlow={true}
                    category="success"
                    icon="award"
                  />
                </div>
                <div className="title">
                  {messages.readerCompletionCompleted}
                </div>
                <div className="description">
                  {messages.readerCompletionCompletedDescription}
                </div>
              </>
            )}
            <div className="dates">
              <div className="completion-date">
                {`${messages.completionsCompletionDate}: ${getFormattedDate({
                  date: completion.completionDate
                })}`}
              </div>
              {completion.expirationDate ? (
                <div className="expiration-date">
                  {`${messages.commonExpirationDate}: ${getFormattedDate({
                    date: completion.expirationDate
                  })}`}
                </div>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="info">
            <div className="badge">
              <ReaderCompletionFormBadge
                category="error"
                icon="exclamation"
                size="small"
              />
            </div>
            <div className="title">{messages.readerCompletionFailed}</div>
            <div className="description">
              {messages.readerCompletionFailedDescription}
            </div>
          </div>
        )}

        {!readOnly &&
        !courseCompletionHideResults &&
        completion &&
        completion.status !== 'waiting-for-reviews' ? (
          <div className="progress">
            {courseRequirements?.numberOfTasks &&
            courseRequirements?.taskMaxPoints > 0 &&
            course.completionTasks > 0 ? (
              <div className="progress-bar">
                <ProgressBar
                  color={
                    completion.tasksProgress * 100 >= course.completionTasks
                      ? theme.successStandaloneColor
                      : theme.dangerColor
                  }
                  label={
                    messages.readerCompletionCorrectAnswers +
                    ` ${Math.round(completion.tasksProgress * 100)} %` +
                    ` (${course.completionTasks} % ${messages.readerCompletionRequired})`
                  }
                  labelStyle={{
                    color: theme.textColorLighter,
                    fontSize: '1em'
                  }}
                  progress={completion.tasksProgress}
                />
              </div>
            ) : null}

            {course.completionReading > 0 ? (
              <div className="progress-bar">
                <ProgressBar
                  color={
                    completion.readingProgress * 100 >= course.completionReading
                      ? theme.successStandaloneColor
                      : theme.dangerColor
                  }
                  label={
                    messages.tasksReading +
                    ` ${Math.round(completion.readingProgress * 100)} %` +
                    ` (${course.completionReading} % ${messages.readerCompletionRequired})`
                  }
                  labelStyle={{
                    color: theme.textColorLighter,
                    fontSize: '1em'
                  }}
                  progress={completion.readingProgress}
                />
              </div>
            ) : null}
            {courseRequirements?.numberOfVideos &&
            course.completionVideos > 0 ? (
              <div className="progress-bar">
                <ProgressBar
                  color={
                    videoRequirementsType === 'individual' &&
                    completion.videosWatched &&
                    videosWatched >= videosWatchedRequirement
                      ? theme.successStandaloneColor
                      : videoRequirementsType === 'individual' &&
                        completion.videosWatched
                      ? theme.dangerColor
                      : completion.videoProgress * 100 >=
                        course.completionVideos
                      ? theme.successStandaloneColor
                      : theme.dangerColor
                  }
                  label={
                    videoRequirementsType === 'individual' &&
                    completion.videosWatched
                      ? messages.completionsVideosWatched +
                        ': ' +
                        completion.videosWatched
                      : messages.tasksVideos +
                        ` ${Math.round(completion.videoProgress * 100)} %` +
                        ` (${course.completionVideos} % ${messages.readerCompletionRequired})`
                  }
                  labelStyle={{
                    color: theme.textColorLighter,
                    fontSize: '1em'
                  }}
                  progress={
                    videoRequirementsType === 'individual' &&
                    completion.videosWatched
                      ? videosWatched / videosWatchedRequirement
                      : completion.videoProgress
                  }
                />
              </div>
            ) : null}
          </div>
        ) : !readOnly &&
          !courseCompletionHideRequirements &&
          completion &&
          completion.status === 'failed' ? (
          <div className="failed-requirements">
            {completion.tasksProgress * 100 < course.completionTasks ? (
              <ReaderCompletionFormRequirement
                fontSize={fontSize}
                icon="check"
                label={messages.completionsFailedTasks}
              />
            ) : null}

            {completion.readingProgress * 100 < course.completionReading ? (
              <ReaderCompletionFormRequirement
                fontSize={fontSize}
                icon="glasses"
                label={messages.completionsFailedReading}
              />
            ) : null}

            {(videoRequirementsType === 'individual' &&
              completion.videosWatched &&
              videosWatched >= videosWatchedRequirement) ||
            (videoRequirementsType === 'combined' &&
              completion.videoProgress * 100 < course.completionVideos) ? (
              <ReaderCompletionFormRequirement
                fontSize={fontSize}
                icon="play"
                label={messages.completionsFailedVideos}
              />
            ) : null}
          </div>
        ) : null}

        <div className="footer">
          {completion && completion.status === 'success' ? (
            <>
              {!readOnly &&
              courseCompletionCustomContent &&
              course.completionFormShowCustomContent ? (
                <div className="completion-custom-content">
                  <CourseCompletionCustomContent
                    course={course}
                    courseCompletion={completion}
                    targetUser={chapterData?.user}
                  />
                </div>
              ) : null}

              <div className="download">
                {course.certificateAllowDownloading ? (
                  <div className="download-cert">
                    <ApiDownloadButton
                      category="light"
                      filename={`${course.slug}-cert.pdf`}
                      icon="file-pdf"
                      size={fontSize as ButtonSize}
                      style={
                        user?.roles.includes('user')
                          ? {
                              borderRadius: `${theme.buttonBorderRadius} 0 0 ${theme.buttonBorderRadius}`
                            }
                          : null
                      }
                      text={messages.completionsDownloadCertificate}
                      url={
                        `/admin/certificate?completionId=${completion.id}` +
                        `&language=${language}`
                      }
                    />
                    <div className="download-dropdown">
                      <Dropdown
                        category="light"
                        label={messages.commonShowMoreOptions}
                        buttonStyle={{
                          borderLeft: `1px solid #fff`,
                          borderRadius: `0 ${theme.buttonBorderRadius} ${theme.buttonBorderRadius} 0`
                        }}
                        icon="chevron-down"
                        size={fontSize as ButtonSize}
                        styleContentButtons={false}
                      >
                        {user?.roles?.includes('user') && user?.email ? (
                          <ApiPostButton
                            data={{
                              completionId: completion.id,
                              emailAddress: user.email,
                              language
                            }}
                            icon="envelope"
                            isDropdown={true}
                            successMessage={
                              messages.completionsSendCertificateSuccess
                            }
                            text={messages.completionsSendCertificate}
                            url="/admin/certificate/send"
                          />
                        ) : user?.roles.includes('guest') || !user?.email ? (
                          <SendToEmailButton
                            apiData={{
                              completionId: completion.id,
                              language
                            }}
                            apiUrl="/admin/certificate/send"
                            buttonText={messages.completionsSendCertificate}
                            id="certificate"
                            isDropdown={true}
                            successMessage={
                              messages.completionsSendCertificateSuccess
                            }
                          />
                        ) : null}
                      </Dropdown>
                    </div>
                  </div>
                ) : null}

                {course.tasksAllowDownloading ? (
                  <div className="download-answers">
                    <FollowUsersDownloadAnswers
                      completionId={completion.id}
                      courseId={course.id}
                      includeReviews={course.tasksIncludeReviews}
                      isAdmin={false}
                      user={chapterData?.user}
                      fromReaderProps={{
                        category: 'light',
                        size: fontSize as ButtonSize,
                        style: user?.roles?.includes('user')
                          ? {
                              borderRadius: `${theme.buttonBorderRadius} 0 0 ${theme.buttonBorderRadius}`
                            }
                          : null
                      }}
                    />

                    <div className="download-dropdown">
                      <Dropdown
                        category="light"
                        buttonStyle={{
                          borderLeft: `1px solid #fff`,
                          borderRadius: `0 ${theme.buttonBorderRadius} ${theme.buttonBorderRadius} 0`
                        }}
                        icon="chevron-down"
                        label={messages.commonShowMoreOptions}
                        size={fontSize as ButtonSize}
                        styleContentButtons={false}
                      >
                        {user?.roles?.includes('user') && user?.email ? (
                          <ApiPostButton
                            data={{ completionId: completion.id, language }}
                            icon="envelope"
                            isDropdown={true}
                            successMessage={messages.tasksSendAnswersSuccess}
                            text={messages.tasksSendAnswers}
                            url={`/admin/users/${chapterData?.user?.id}/courses/${course.id}/answers/send`}
                          />
                        ) : user?.roles.includes('guest') || !user?.email ? (
                          <SendToEmailButton
                            apiData={{
                              completionId: completion.id,
                              language
                            }}
                            apiUrl={`/admin/users/${chapterData?.user?.id}/courses/${course.id}/answers/send`}
                            buttonText={messages.tasksSendAnswers}
                            id="answers"
                            isDropdown={true}
                            successMessage={messages.tasksSendAnswersSuccess}
                          />
                        ) : null}
                      </Dropdown>
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          ) : !readOnly &&
            completion &&
            completion.status === 'failed' &&
            course.completionAllowRetries ? (
            <>
              {completion.tasksProgress * 100 < course.completionTasks ? (
                <ConfirmButton
                  actionText={messages.commonConfirm}
                  buttonText={messages.commonTryAgain}
                  confirmText={messages.readerCompletionBackWarning}
                  confirmTitle={messages.commonTryAgain}
                  onConfirm={onRetry}
                  stopPropagation={true}
                />
              ) : (
                <Button
                  onClick={async () => {
                    try {
                      await onRetry();
                    } catch (err) {
                      await dispatch.handleErrorDispatch(err);
                    }
                  }}
                  size={fontSize as ButtonSize}
                  text={messages.commonTryAgain}
                />
              )}
            </>
          ) : null}
        </div>
      </div>

      <style jsx>{`
        .content {
          margin: 20px auto;
          max-width: 360px;
        }
        .completion-custom-content {
          margin-bottom: 15px;
        }
        .info {
          margin-bottom: 30px;
          text-align: center;
        }
        .badge {
          display: inline-block;
          margin: 0 0 20px;
        }
        .title {
          color: ${theme.textColor};
          font-size: 1.1em;
          font-weight: 700;
          margin-bottom: 2px;
        }
        .description {
          color: ${theme.textColorLighter};
          font-weight: 600;
          margin: 0 auto 5px;
          max-width: 260px;
          text-align: center;
        }
        .dates {
          color: ${theme.textColorLighter};
          font-size: 0.9em;
          font-weight: 600;
        }
        .progress-bar {
          margin-top: 20px;
        }
        .failed-requirements {
          color: ${theme.textColorLighter};
          font-weight: 600;
        }
        .footer {
          margin-top: 40px;
          text-align: center;
        }
        .download-answers {
          margin-top: 15px;
        }
        .download-dropdown {
          display: inline-block;
        }
      `}</style>
    </div>
  );
};

export default ReaderCompletionFormResult;
